import React, { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import {
	SOURCE_FILTER_OPTIONS,
	SOURCE_TYPE_LABEL,
	getQueryLabel,
	sourceSubtitle,
	sourceUrlLabel,
} from "./fanoutUtils";

export default function FanoutSourcesView({ analysis }) {
	const [filter, setFilter] = useState("all");

	const sources = useMemo(() => {
		const list = (analysis?.sources || []).filter(
			(source) => source.citation_type !== "mentioned"
		);
		const typeOrder = { cited: 0, fetched: 1 };
		list.sort((a, b) => {
			const typeDiff =
				(typeOrder[a.citation_type] ?? 2) - (typeOrder[b.citation_type] ?? 2);
			if (typeDiff !== 0) return typeDiff;
			return String(a.url || a.domain || "").localeCompare(String(b.url || b.domain || ""));
		});
		return list;
	}, [analysis?.sources]);

	const filtered = useMemo(() => {
		if (filter === "all") return sources;
		return sources.filter((source) => source.citation_type === filter);
	}, [filter, sources]);

	return (
		<div className="monitoring__fanout-sources-view monitoring__fanout-view-panel">
			<div className="monitoring__fanout-sources-toolbar">
				<h4 className="monitoring__analysis-panel-title mb-0">Sources catalog</h4>
				<div
					className="monitoring__segmented monitoring__segmented--sm"
					role="group"
					aria-label="Source type filter"
				>
					{SOURCE_FILTER_OPTIONS.map((option) => (
						<button
							key={option.id}
							type="button"
							className={`monitoring__segmented-btn${
								filter === option.id ? " is-active" : ""
							}`}
							onClick={() => setFilter(option.id)}
							aria-pressed={filter === option.id}
						>
							{option.label}
							<span className="monitoring__fanout-sources-count">
								{option.id === "all"
									? sources.length
									: sources.filter((item) => item.citation_type === option.id).length}
							</span>
						</button>
					))}
				</div>
			</div>
			{filtered.length === 0 ? (
				<p className="text-muted small mb-0 mt-3">No sources match this filter.</p>
			) : (
				<ul className="monitoring__fanout-sources-table">
					{filtered.map((source) => {
						const type = source.citation_type;
						return (
							<li key={source.id} className="monitoring__fanout-sources-row">
								<span
									className={`monitoring__badge monitoring__badge--${type} monitoring__fanout-sources-type`}
								>
									{SOURCE_TYPE_LABEL[type] || type}
								</span>
								<div className="monitoring__fanout-sources-main">
									<a
										href={source.url}
										target="_blank"
										rel="noopener noreferrer"
										className="monitoring__fanout-sources-link"
									>
										{sourceUrlLabel(source)}
										<ExternalLink size={12} aria-hidden />
									</a>
									{sourceSubtitle(source) ? (
										<div className="monitoring__fanout-sources-sub">
											{sourceSubtitle(source)}
										</div>
									) : null}
									<div className="monitoring__fanout-sources-meta">
										{getQueryLabel(analysis, source.query_id)}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
